// Lib
import { first, uniq } from 'lodash';

// Utils
import globalLogger from '../../../logger/logger';
import { getFullPath } from '../../../../common/dataStructures/treeUtils';
import { isLocationTrash, removeTrashSuffix } from '../../../../common/elements/utils/elementLocationUtils';
import { isBoard } from '../../../../common/elements/utils/elementTypeUtils';
import { findParentIds, isValidAliasParent } from './breadcrumbAliasUtil';
import {
    createCompleteBreadcrumb,
    createProtectedBreadcrumb,
    getReadableBreadcrumb,
    getReadablePath,
} from './breadcrumbResultUtil';

// Types
import { IdGraph, IdTree } from '../../../../common/dataStructures/graphTypes';
import { ImMNElementMap } from '../../../../common/elements/elementModelTypes';
import { BreadcrumbResult, PreferredAliasIdByBoardId } from '../breadcrumbTypes';

const logger = globalLogger.createChannel('breadcrumb-util');

const MAX_BREADCRUMB_ITERATIONS = 300;

let hasReportedError = false;

/**
 * Normalises IDs before fetching because trash locations use virtual parent IDs that the board API can't resolve.
 */
const normalizeMissingElementIds = (elementIds: string[], elements: ImMNElementMap): string[] =>
    uniq(
        elementIds
            .map((elementId) => {
                const normalizedElementId = removeTrashSuffix(elementId);
                const element = elements.get(normalizedElementId);

                if (!normalizedElementId) return;
                if (!element) return normalizedElementId;
                if (!isBoard(element)) return;
                if (!elements.get(elementId) || isLocationTrash(element)) return normalizedElementId;
            })
            .filter((id): id is string => Boolean(id)),
    );

/**
 * Retrieves a breadcrumb by walking parent and alias relationships breadth-first.
 */
export const getBreadthFirstBreadcrumb = (
    currentBoardId: string,
    rootBoardId: string,
    elements: ImMNElementMap,
    aclIds: string[],
    boardIdToAliasIdsMap: IdGraph,
    preferredAliasIdByBoardId?: PreferredAliasIdByBoardId,
): BreadcrumbResult => {
    if (!currentBoardId) {
        return {
            complete: false,
            detached: false,
            error: true,
            missingElementIds: normalizeMissingElementIds([currentBoardId], elements),
        };
    }

    // Perform a breadth-first search of elements, starting at the current board and walking backwards through
    // physical parents and aliases until we find the root board or discover that more elements need fetching.
    let elementId: string | null = currentBoardId;
    let queue = [elementId];
    let missingElementIds = [] as string[];
    let iterationCount = 0;

    const visitedElementIds = {} as { [key: string]: boolean };
    const previousNodes = { [elementId]: null } as IdTree;

    const addToPreviousNodes = (id: string) => {
        previousNodes[id] = previousNodes[id] || elementId;
    };

    // Guest users don't have a rootBoardId, so they skip this and fall back to the public board calculation.
    while (rootBoardId && queue.length && iterationCount < MAX_BREADCRUMB_ITERATIONS) {
        iterationCount++;
        elementId = queue.shift() as string;

        // Avoid revisiting aliases or parent boards when cycles exist in the traversed graph.
        if (visitedElementIds[elementId]) continue;

        visitedElementIds[elementId] = true;

        if (elementId === rootBoardId) {
            const fullPath = getFullPath(previousNodes, elementId);
            return createCompleteBreadcrumb(fullPath, elements, false);
        }

        // Check all adjacent nodes: physical parents plus aliases that point to this board.
        const parentIds = findParentIds(elements, boardIdToAliasIdsMap, elementId, preferredAliasIdByBoardId);

        parentIds.filter((parentId) => !visitedElementIds[parentId]).forEach(addToPreviousNodes);
        queue = queue.concat(parentIds);
        // Track missing elements in case the current state is too incomplete to prove the breadcrumb path yet.
        missingElementIds = missingElementIds.concat(
            normalizeMissingElementIds([currentBoardId, ...parentIds], elements),
        );
    }

    if (iterationCount >= MAX_BREADCRUMB_ITERATIONS) {
        if (!hasReportedError) {
            logger.error('Failed to build breadcrumbs (iteration limit reached)', {
                aclIds,
                currentBoardId,
            });
            hasReportedError = true;
        }

        const sharedPath = getReadablePath(currentBoardId, elements, aclIds);

        return createCompleteBreadcrumb(sharedPath, elements, true, { error: true });
    }

    // If we couldn't find the target but found missing elements, fetch those before treating the breadcrumb as complete.
    if (missingElementIds.length) {
        return {
            complete: false,
            detached: false,
            missingElementIds: uniq(missingElementIds),
        };
    }

    // If the root couldn't be found and there aren't any missing elements, use the highest readable shared path.
    const sharedPath = getReadablePath(currentBoardId, elements, aclIds);

    if (!sharedPath.length) return createProtectedBreadcrumb(currentBoardId);

    return createCompleteBreadcrumb(sharedPath, elements, true);
};

/**
 * Handles the two most common breadcrumb scenarios:
 *  - The current board is within the user's physical tree.
 *  - The board is within a shared board, and the user has a single alias to the shared board.
 *
 * More complex alias paths fall through to the breadth-first search.
 */
export const getPhysicalOrSingleSharedBoardBreadcrumb = (
    currentBoardId: string,
    rootBoardId: string,
    elements: ImMNElementMap,
    aclIds: string[],
    boardIdToAliasIdsMap: IdGraph,
): BreadcrumbResult | undefined => {
    const readableBreadcrumb = getReadableBreadcrumb(currentBoardId, rootBoardId, elements, aclIds);

    if (!readableBreadcrumb) return;

    const readablePath = readableBreadcrumb.fullPath;
    const readableRootId = first(readablePath) as string;

    if (readableRootId === rootBoardId) return readableBreadcrumb;

    // Otherwise, get the alias for the readable root and then get that alias's physical path.
    const aliasIds = boardIdToAliasIdsMap[readableRootId];

    // Multiple aliases require navigation preference or breadth-first search to choose the right path.
    if (!aliasIds || aliasIds.length !== 1) return;

    const aliasId = first(aliasIds) as string;

    if (!isValidAliasParent(elements, aliasId, readableRootId)) return;
    const aliasReadableBreadcrumb = getReadableBreadcrumb(aliasId, rootBoardId, elements, aclIds);

    if (!aliasReadableBreadcrumb || aliasReadableBreadcrumb.detached) return;

    const fullPath = [...aliasReadableBreadcrumb.fullPath, ...readablePath];

    return createCompleteBreadcrumb(fullPath, elements, false);
};
